import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Users, Power, PowerOff, Trophy, Zap, Target } from 'lucide-react';
import apiService from '../../services/api';
import { Button, AdminPageLayout, ConfirmDialog, Section } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import { useToast } from '../../contexts/ToastContext';

const ProgramsManagement = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    category: 'all'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const PROGRAM_CATEGORIES = [
    { value: 'competitive', label: 'Competitive' },
    { value: 'recreational', label: 'Recreational' },
    { value: 'development', label: 'Development' }
  ];

  const PROGRAM_STATUSES = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' }
  ];

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPrograms();
      setPrograms(response.programs || []);
    } catch (err) {
      setError(err.message || 'Failed to load programs');
      showError(`Error loading programs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleDeleteProgram = async () => {
    if (!programToDelete) return;

    try {
      await apiService.deleteProgram(programToDelete.id);
      showSuccess(`Program "${programToDelete.name}" deleted successfully`);
      await loadPrograms();
      setShowDeleteConfirm(false);
      setProgramToDelete(null);
    } catch (err) {
      showError(err.message || 'Failed to delete program');
    }
  };

  const handleStatusAction = async (action, program) => {
    try {
      setStatusLoading(true);
      setError(null);

      const endpoint = action === 'activate' ? 'activate' : 'deactivate';
      await apiService.request(`/api/programs/${program.id}/${endpoint}`, { method: 'POST' });

      showSuccess(`Program "${program.name}" ${action}d successfully`);
      await loadPrograms();
      setStatusAction(null);
    } catch (err) {
      showError(err.message || `Failed to ${action} program`);
    } finally {
      setStatusLoading(false);
    }
  };

  const filteredPrograms = programs.filter(program => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!program.name?.toLowerCase().includes(searchTerm) &&
        !program.description?.toLowerCase().includes(searchTerm) &&
        !program.category?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    if (filters.status !== 'all' && program.status !== filters.status) {
      return false;
    }
    if (filters.category !== 'all' && program.category !== filters.category) {
      return false;
    }
    return true;
  });

  const getProgramIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'competitive': return <Target size={32} />;
      case 'recreational': return <Trophy size={32} />;
      case 'development': return <Zap size={32} />;
      default: return <Trophy size={32} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'draft': return 'primary';
      default: return 'secondary';
    }
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search by name, description, or category..."
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            ...PROGRAM_STATUSES
          ]
        },
        {
          name: 'category',
          label: 'Category',
          options: [
            { value: 'all', label: 'All Categories' },
            ...PROGRAM_CATEGORIES
          ]
        }
      ]}
      activeFilters={{ status: filters.status, category: filters.category }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', status: 'all', category: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction onClick={() => navigate('/admin/programs/new')} label="New Program" />
      }
    />
  );

  return (
    <AdminPageLayout
      title="PROGRAM MANAGEMENT"
      subtitle="Manage training programs and teams"
      backPath="/admin/club"
      maxWidth="1400px"
    >
      <Section title="PROGRAMS">
        <Listing
          loading={loading}
          items={filteredPrograms}
          actions={headerActions}
          filters={filterBar}
          renderItem={(program) => (
            <ListingItem
              key={program.id}
              title={program.name}
              subtitle={program.description || `${program.category || 'General'} Program`}
              icon={getProgramIcon(program.category)}
              iconVariant={getStatusColor(program.status)}
              actions={
                <div style={{ display: 'flex', gap: '8px' }}>
                  <EditAction
                    size="xs"
                    onClick={() => navigate(`/admin/programs/${program.id}/edit`)}
                  />
                  <DeleteAction
                    size="xs"
                    onClick={() => {
                      setProgramToDelete(program);
                      setShowDeleteConfirm(true);
                    }}
                  />
                </div>
              }
            >
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.875rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Category</div>
                  <div style={{ fontWeight: '500' }}>{program.category || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Status</div>
                  <div style={{ fontWeight: '500', textTransform: 'capitalize' }}>{program.status || 'N/A'}</div>
                </div>
                {(program.min_age || program.max_age) && (
                  <div>
                    <div style={{ color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Age</div>
                    <div style={{ fontWeight: '500' }}>
                      {program.min_age && program.max_age
                        ? `${program.min_age}-${program.max_age}`
                        : program.min_age
                          ? `${program.min_age}+`
                          : `Up to ${program.max_age}`
                      } yrs
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Teams</div>
                  <div style={{ fontWeight: '500', cursor: 'pointer' }} onClick={() => navigate(`/admin/programs/${program.id}/teams`)}>
                    {program.team_count || 0}
                  </div>
                </div>
              </div>
              {/* Status actions */}
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {program.status === 'draft' && (
                  <Button
                    onClick={() => setStatusAction({ action: 'activate', program })}
                    variant="success"
                    size="xs"
                    disabled={statusLoading}
                  >
                    ACTIVATE
                  </Button>
                )}
                {program.status === 'active' && (
                  <Button
                    onClick={() => setStatusAction({ action: 'deactivate', program })}
                    variant="warning"
                    size="xs"
                    disabled={statusLoading}
                  >
                    DEACTIVATE
                  </Button>
                )}
                {program.status === 'inactive' && (
                  <Button
                    onClick={() => setStatusAction({ action: 'activate', program })}
                    variant="success"
                    size="xs"
                    disabled={statusLoading}
                  >
                    REACTIVATE
                  </Button>
                )}
              </div>
            </ListingItem>
          )}
          emptyMessage="No programs found"
        />
      </Section>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setProgramToDelete(null);
        }}
        onConfirm={handleDeleteProgram}
        title="DELETE PROGRAM"
        message={`Are you sure you want to delete "${programToDelete?.name}"?`}
        details="This action cannot be undone and will only work if there are no active teams."
        variant="danger"
        confirmText="DELETE"
        cancelText="CANCEL"
      />

      <ConfirmDialog
        isOpen={!!statusAction}
        onClose={() => setStatusAction(null)}
        onConfirm={() => {
          if (statusAction) {
            handleStatusAction(statusAction.action, statusAction.program);
          }
        }}
        title={statusAction?.action === 'activate' ? 'ACTIVATE PROGRAM' : 'DEACTIVATE PROGRAM'}
        message={`${statusAction?.action === 'activate' ? 'Activate' : 'Deactivate'} "${statusAction?.program?.name}"?`}
        details={statusAction?.action === 'activate'
          ? 'This program will be set to active status and available for team creation and player registration.'
          : 'This program will be deactivated. Existing teams and enrollments will remain but new registrations will be disabled.'
        }
        variant={statusAction?.action === 'activate' ? 'success' : 'warning'}
        confirmText={statusAction?.action === 'activate' ? 'ACTIVATE' : 'DEACTIVATE'}
        cancelText="CANCEL"
      />
    </AdminPageLayout>
  );
};

export default ProgramsManagement;
